/**
 * Service for managing Client API operations
 * Implements all endpoints from the Client API Frontend Integration Guide
 */

import { makeAuthenticatedRequest } from './backendApiService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

const CLIENT_BASE_URL = '/api/clients';

/**
 * Helper to convert dates in client objects from UTC to local
 * @param {Object|Array} data - Client or array of clients
 * @returns {Object|Array} Data with converted dates
 */
const convertClientDates = (data) => {
    return convertUTCDatesToLocal(data, ['createdAt', 'lastModifiedAt', 'updatedAt']);
};

/**
 * Client Service Class
 */
class ClientService {
    /**
     * Get all clients (active and inactive)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of client objects
     */
    async getAll(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, CLIENT_BASE_URL);
            return convertClientDates(response);
        } catch (error) {
            console.error('Failed to fetch all clients:', error);
            throw error;
        }
    }

    /**
     * Get only active clients
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of active client objects
     */
    async getActive(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/active`);
            return convertClientDates(response);
        } catch (error) {
            console.error('Failed to fetch active clients:', error);
            throw error;
        }
    }

    /**
     * Get only inactive clients
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of inactive client objects
     */
    async getInactive(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/inactive`);
            return convertClientDates(response);
        } catch (error) {
            console.error('Failed to fetch inactive clients:', error);
            throw error;
        }
    }

    /**
     * Get a specific client by ID
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @returns {Promise<Object>} Client object
     */
    async getById(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/${id}`);
            return convertClientDates(response);
        } catch (error) {
            console.error(`Failed to fetch client ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get a specific client by client code
     * @param {string} accessToken - JWT access token
     * @param {string} clientCode - Client code (max 6 chars)
     * @returns {Promise<Object>} Client object
     */
    async getByCode(accessToken, clientCode) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/code/${clientCode}`);
            return convertClientDates(response);
        } catch (error) {
            console.error(`Failed to fetch client with code ${clientCode}:`, error);
            throw error;
        }
    }

    /**
     * Search clients by name (case-insensitive partial match)
     * @param {string} accessToken - JWT access token
     * @param {string} searchTerm - Search term for client name
     * @returns {Promise<Array>} Array of matching client objects
     */
    async search(accessToken, searchTerm) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_BASE_URL}/search?name=${encodeURIComponent(searchTerm)}`
            );
            return convertClientDates(response);
        } catch (error) {
            console.error(`Failed to search clients with term "${searchTerm}":`, error);
            throw error;
        }
    }

    /**
     * Create a new client
     * @param {string} accessToken - JWT access token
     * @param {Object} clientData - Client data object
     * @param {string} [clientData.clientCode] - Optional client code (max 6 chars, unique)
     * @param {string} [clientData.clientName] - Optional client name (max 50 chars)
     * @param {boolean} [clientData.isActive] - Optional active status (defaults to true)
     * @returns {Promise<Object>} Created client object with ID and timestamps
     */
    async create(accessToken, clientData) {
        try {
            // Validate that at least one of clientCode or clientName is provided
            if (!clientData.clientCode && !clientData.clientName) {
                throw new Error('Either client code or client name must be provided');
            }

            // Validate clientCode length
            if (clientData.clientCode && clientData.clientCode.length > 6) {
                throw new Error('Client code cannot exceed 6 characters');
            }

            // Validate clientName length
            if (clientData.clientName && clientData.clientName.length > 50) {
                throw new Error('Client name cannot exceed 50 characters');
            }

            const response = await makeAuthenticatedRequest(accessToken, CLIENT_BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(clientData)
            });
            return convertClientDates(response);
        } catch (error) {
            console.error('Failed to create client:', error);
            throw error;
        }
    }

    /**
     * Update an existing client
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @param {Object} clientData - Updated client data object
     * @returns {Promise<Object>} Updated client object
     */
    async update(accessToken, id, clientData) {
        try {
            // Apply same validation as create
            if (!clientData.clientCode && !clientData.clientName) {
                throw new Error('Either client code or client name must be provided');
            }

            if (clientData.clientCode && clientData.clientCode.length > 6) {
                throw new Error('Client code cannot exceed 6 characters');
            }

            if (clientData.clientName && clientData.clientName.length > 50) {
                throw new Error('Client name cannot exceed 50 characters');
            }

            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(clientData)
            });
            return convertClientDates(response);
        } catch (error) {
            console.error(`Failed to update client ${id}:`, error);
            throw error;
        }
    }

    /**
     * Delete a client
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @returns {Promise<void>}
     */
    async delete(accessToken, id) {
        try {
            await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/${id}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error(`Failed to delete client ${id}:`, error);
            throw error;
        }
    }

    /**
     * Activate a client (sets isActive to true)
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @returns {Promise<Object>} Updated client object
     */
    async activate(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/${id}/activate`, {
                method: 'PUT'
            });
            return convertClientDates(response);
        } catch (error) {
            console.error(`Failed to activate client ${id}:`, error);
            throw error;
        }
    }

    /**
     * Deactivate a client (sets isActive to false)
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @returns {Promise<Object>} Updated client object
     */
    async deactivate(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, `${CLIENT_BASE_URL}/${id}/deactivate`, {
                method: 'PUT'
            });
            return convertClientDates(response);
        } catch (error) {
            console.error(`Failed to deactivate client ${id}:`, error);
            throw error;
        }
    }

    /**
     * Check if a client code already exists
     * @param {string} accessToken - JWT access token
     * @param {string} clientCode - Client code to check
     * @returns {Promise<boolean>} True if code exists, false otherwise
     */
    async checkCodeExists(accessToken, clientCode) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_BASE_URL}/exists/code/${clientCode}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to check if client code "${clientCode}" exists:`, error);
            throw error;
        }
    }

    /**
     * Find clients with similar names to help avoid duplicate entries
     * @param {string} accessToken - JWT access token
     * @param {string} name - Client name to check for similarity
     * @param {number} threshold - Similarity threshold between 0.0 and 1.0 (default: 0.8)
     * @returns {Promise<Array>} Array of clients with similar names
     */
    async findSimilarNames(accessToken, name, threshold = 0.7) {
        try {
            if (!name || name.trim().length === 0) {
                throw new Error('Name parameter is required');
            }

            if (threshold < 0.0 || threshold > 1.0) {
                throw new Error('Threshold must be between 0.0 and 1.0');
            }

            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${CLIENT_BASE_URL}/similar-names?name=${encodeURIComponent(name.trim())}&threshold=${threshold}`
            );
            return response;
        } catch (error) {
            console.error(`Failed to find similar client names for "${name}":`, error);
            throw error;
        }
    }

    /**
     * Generate a unique client code based on client name
     * @param {string} accessToken - JWT access token
     * @param {string} name - Client name to generate code from
     * @returns {Promise<string|null>} Generated client code or null if none available
     */
    async generateCode(accessToken, name) {
        try {
            if (!name || name.trim().length === 0) {
                throw new Error('Name parameter is required');
            }

            const response = await makeAuthenticatedRequest(
                accessToken,
                `${CLIENT_BASE_URL}/generateclientcode`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ name: name.trim() })
                }
            );
            
            // Backend returns JSON: { "code": "NGM" } or { "code": "" }
            const code = response?.code?.trim();
            
            // Return the code if it's a non-empty string
            return code && code.length > 0 ? code : null;
        } catch (error) {
            // If we get a 204 No Content or any error, return null
            if (error.response && error.response.status === 204) {
                return null;
            }
            console.error(`Failed to generate client code for "${name}":`, error);
            // Return null instead of throwing to allow graceful fallback
            return null;
        }
    }

    /**
     * Get client statistics (for dashboard)
     * OPTIMIZED: Uses only ONE API call instead of three to eliminate backend spamming
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} Statistics object with counts
     */
    async getStatistics(accessToken) {
        try {
            // OPTIMIZED: Use only ONE call instead of three separate calls
            const allClients = await this.getAll(accessToken);
            
            // Filter active and inactive clients from the single dataset
            const activeClients = allClients.filter(client => client.isActive);
            const inactiveClients = allClients.filter(client => !client.isActive);

            // Calculate clients created in the last 30 days
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            const recentClients = allClients.filter(client => {
                if (!client.createdAt) return false;
                const createdDate = new Date(client.createdAt);
                return createdDate >= thirtyDaysAgo;
            });

            return {
                total: allClients.length,
                active: activeClients.length,
                inactive: inactiveClients.length,
                recentClients: recentClients.length
            };
        } catch (error) {
            console.error('Failed to fetch client statistics:', error);
            throw error;
        }
    }

    /**
     * Link a client to a QuickBooks customer
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @param {string} quickBooksCustomerId - QuickBooks Customer ID
     * @returns {Promise<Object>} Updated client object
     */
    async linkToQuickBooks(accessToken, id, quickBooksCustomerId) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${CLIENT_BASE_URL}/${id}/quickbooks/${encodeURIComponent(quickBooksCustomerId)}`,
                {
                    method: 'PUT'
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to link client ${id} to QuickBooks customer ${quickBooksCustomerId}:`, error);
            throw error;
        }
    }

    /**
     * Unlink a client from QuickBooks
     * @param {string} accessToken - JWT access token
     * @param {number} id - Client ID
     * @returns {Promise<Object>} Updated client object
     */
    async unlinkFromQuickBooks(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${CLIENT_BASE_URL}/${id}/quickbooks`,
                {
                    method: 'DELETE'
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to unlink client ${id} from QuickBooks:`, error);
            throw error;
        }
    }
}

// Export singleton instance
export const clientService = new ClientService();

// Export individual methods for convenience
export const {
    getAll: getAllClients,
    getActive: getActiveClients,
    getInactive: getInactiveClients,
    getById: getClientById,
    getByCode: getClientByCode,
    search: searchClients,
    create: createClient,
    update: updateClient,
    delete: deleteClient,
    activate: activateClient,
    deactivate: deactivateClient,
    checkCodeExists: checkClientCodeExists,
    findSimilarNames: findSimilarClientNames,
    generateCode: generateClientCode,
    getStatistics: getClientStatistics,
    linkToQuickBooks: linkClientToQuickBooks,
    unlinkFromQuickBooks: unlinkClientFromQuickBooks
} = clientService;

export default clientService;
